import { type PlacementInfo } from 'src/components/pages/placementDetails/updatePlacementStatusModal/constants';
import { AccommodationStatusIds } from 'src/config/AccommodationStatusId';
import type { AssociateProfilePlacement } from 'src/services/associateProfile/types';
import {
  type AccommodationStatusResponseDto,
  type PlacementDetails,
  type PlacementDetailsAllocatedJobShift,
  type PlacementDetailsAppliedModification,
} from 'src/store/placementDetailsPageSlice/types';

// **** Types **** //

export interface ModificationListItem {
  modificationText: string;
  taskNames: string[];
}

/**
 * Map a Redux PlacementDetails into the shared PlacementInfo the update menu consumes. The placement id
 * (from the route) and statusOptions (from a separate slice value) are supplied by the caller.
 */
export const placementDetailsToPlacementInfo = (placementId: string, d: PlacementDetails): PlacementInfo => ({
  placementId,
  regionId: d.regionId,
  accommodationType: d.accommodationType,
  placementStatus: d.accommodationStatus?.status,
  externalCaseId: d.externalCaseId,
  managerAlias: d.managerAlias,
  fc: d.fc,
  countryId: d.countryId,
  associateName: d.associateFullName || d.login,
  modifiedShiftLength: d.modifiedShiftLength,
  workRestrictions: d.workRestrictions,
  statusOptions: d.nextStatuses ?? [],
  isSiteParticipatingInDecisionHubPilot: d.site?.participatingInDecisionHubPilot ?? false,
});

/**
 * Map an associate profile placement into the shared PlacementInfo. regionId/countryId come from the
 * placement's site; statusOptions is supplied by the caller.
 */
export const associatePlacementToPlacementInfo = (p: AssociateProfilePlacement): PlacementInfo => ({
  placementId: p.accommodationId.toString(),
  regionId: p.site.regionId,
  accommodationType: p.accommodationType,
  placementStatus: p.accommodationStatus,
  externalCaseId: p.externalCaseId,
  managerAlias: p.managerAlias,
  fc: p.fc,
  countryId: p.site.countryId,
  associateName: p.associateFullName || p.alias,
  modifiedShiftLength: p.modifiedShiftLength,
  workRestrictions: p.workRestrictions,
  statusOptions: p.nextStatuses ?? [],
  isSiteParticipatingInDecisionHubPilot: p.site?.participatingInDecisionHubPilot ?? false,
});

/**
 * Get selectable statuses for update modal
 */
export const filterIndefiniteRestrictionStatuses = (accommodationStatus?: AccommodationStatusResponseDto[]) => {
  const INDEFINITE_RESTRICTIONS_TRANSITION_SET = new Set<number>([
    AccommodationStatusIds.RETURN_TO_FULL_DUTY,
    AccommodationStatusIds.INDEFINITE_RESTRICTIONS,
  ]);
  return accommodationStatus?.filter(({ id }) => INDEFINITE_RESTRICTIONS_TRANSITION_SET.has(id));
};

/**
 * Convert a single or "list of" AllocatedJobShifts to a user readable list of modifications.
 */
export const allocatedJobShiftsToModificationDisplayList = (
  allocatedJobShifts: PlacementDetailsAllocatedJobShift | PlacementDetailsAllocatedJobShift[],
): ModificationListItem[] => {
  // Get the list of applied modifications
  const jobsShifts = Array.isArray(allocatedJobShifts) ? allocatedJobShifts : [allocatedJobShifts];
  const allAppliedMods = jobsShifts.flatMap((ajs) => ajs.appliedModifications);
  // Group the modifications by name and free form text
  const groupedMods = new Map<string, PlacementDetailsAppliedModification[]>();
  allAppliedMods.forEach((appliedMod) => {
    const { modificationName, freeFormText } = appliedMod;
    const fft = freeFormText ? ': ' + freeFormText : '';
    const key = `${modificationName}${fft}`;
    const group = groupedMods.get(key) ?? [];
    group.push(appliedMod);
    groupedMods.set(key, group);
  });
  // Setup the result
  return Array.from(groupedMods.entries())
    .map(([key, appliedMods]) => ({
      modificationText: key,
      taskNames: [...new Set(appliedMods.map((am) => am.taskName))].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => a.modificationText.localeCompare(b.modificationText));
};
